/**
 * Per-destination shipping and returns policy — the single source of truth for
 * every place the storefront publishes those numbers: the Product JSON-LD's
 * `shippingDetails` / `hasMerchantReturnPolicy` and the PDP info cards. Change
 * the numbers here and nowhere else.
 *
 * These are NOT an independent policy: every figure below was read off the
 * Vendure courier configuration the checkout actually prices against, per
 * channel, via `eligibleShippingMethods { price priceWithTax courierConfig {
 * countriesConfigs { country freeShippingPrice pricesConfigs { maxWeight price
 * } } } }`. That query is why the table is copied here rather than fetched: it
 * hangs off `ShippingMethodQuote`, which the backend only produces for an
 * ACTIVE order with a destination country (see `useFreeShippingProgress`), and
 * a product page has neither. The one cart-free query, `activeShippingMethods`,
 * returns `PublicShippingMethod`, which carries no price at all.
 *
 * The free-shipping threshold is NOT here any more: it is a Dashboard setting
 * (Settings → Free delivery threshold, read through `useFreeShippingThresholds`),
 * so moving it takes no code change, and the backend prices by the same setting.
 * Re-check with `./verify-market-policy.ts` (in this app's root) whenever
 * couriers are re-priced or the free-delivery settings change: it replays that
 * quote against every channel, diffs the rates against this file, and flags a
 * destination advertised free delivery that none of its methods gives.
 *
 * The rates are also written out in `content/<locale>/delivery.md`, which
 * `shippingSettingsLink` points Google at. Keep the two in step.
 */

/** Destinations this storefront publishes shipping and returns policy for. */
export type MarketCountry = 'CZ' | 'SK' | 'PL' | 'DE' | 'AT'

export interface MarketShipping {
  country: MarketCountry
  /**
   * ISO 4217 for every amount in this row. Must equal the channel currency of
   * the locale serving this country — the JSON-LD drops any market whose
   * currency differs from the offer's, because a `shippingRate` priced in a
   * currency the offer is not is worse than no shipping data at all.
   */
  currency: 'CZK' | 'EUR' | 'PLN'
  /**
   * Cheapest standard delivery, minor units, VAT-inclusive — the same basis as
   * `variant.price` (every channel has `pricesIncludeTax: true`). "Standard"
   * means the couriers that take part in the free-shipping programme; the
   * express tiers (FedEx, DHL Express) are excluded exactly as the delivery
   * pages exclude them, since free shipping never applies to those.
   */
  baseRateMinor: number
  /** Working days in transit after dispatch, as the delivery pages state them. */
  transitDays: { min: number, max: number }
  /** Path (locale-relative) of the page backing these figures. */
  shippingPage: string
}

export interface MarketReturns {
  /** Every destination this one policy applies to. */
  countries: MarketCountry[]
  /** Days from delivery, matching `merchantReturnDays`. */
  windowDays: number
  /** `true` → schema.org `FreeReturn`; the seller pays the return carriage. */
  free: boolean
  /** Path (locale-relative) of the page backing these terms. */
  policyPage: string
}

/** Warehouse local time. The dispatch cutoff is Prague's afternoon, not the shopper's. */
export const WAREHOUSE_TIME_ZONE = 'Europe/Prague'

/**
 * Orders placed before this hour (warehouse time) still go out the same day,
 * with Friday closing an hour earlier than the rest of the week.
 *
 * Both hours live here rather than in `useDeliveryEstimate`, which imports
 * them. They used to be declared in both places — this file exporting a
 * `WAREHOUSE_CUTOFF_HOUR` that nothing read, while the estimate ran off a
 * private copy — so the file that tells you to change the numbers here and
 * nowhere else held the one copy that changed nothing.
 *
 * `HANDLING_DAYS` below spans both hours, so the policy figures Google is given
 * stay correct whichever day the shopper reads them on.
 */
export const SAME_DAY_CUTOFF_HOUR = 14
export const FRIDAY_CUTOFF_HOUR = 13

/**
 * Working days between order and dispatch. Zero when the order beats the
 * cutoff on a working day and one otherwise — the rule `useDeliveryEstimate`
 * implements and the "processing time is 1 working day" every delivery page
 * states, expressed as the range `handlingTime` wants.
 *
 * A public holiday can push dispatch further than `max` — a Christmas Eve order
 * does not leave until the 27th. Left at 1 deliberately: this is the figure
 * Google publishes as the ordinary case, and Search Console rejects a
 * `handlingTime` whose maximum is inflated to cover a fortnight nobody is
 * shopping in. The per-order truth is `useDeliveryEstimate`'s date.
 */
export const HANDLING_DAYS = { min: 0, max: 1 } as const

/**
 * Fixed-date Czech public holidays, `MM-DD`. The warehouse is in Prague, so
 * this is the calendar that decides whether a parcel can be dispatched at all,
 * whatever market the shopper is in.
 *
 * Deliberately the Czech list only, applied to the transit leg as well as
 * dispatch, even though the destination's own holidays are what actually stop a
 * courier abroad. The markets diverge — 28.10 is Czech-only, 11.11 is Polish,
 * 1.11 is a holiday in SK/PL/DE but not here — so on those days a foreign order
 * is quoted a day slower than it really is. That is the forgiving direction,
 * and the same one `DEFAULT_TRANSIT_WORKING_DAYS` already chose: an estimate
 * that arrives early costs nothing, a late one costs a complaint. The days that
 * matter most are 25.12 and 26.12, which are holidays in all four markets.
 */
const FIXED_WAREHOUSE_HOLIDAYS = [
  '01-01', // Nový rok / Den obnovy samostatného českého státu
  '05-01', // Svátek práce
  '05-08', // Den vítězství
  '07-05', // Den slovanských věrozvěstů Cyrila a Metoděje
  '07-06', // Den upálení mistra Jana Husa
  '09-28', // Den české státnosti
  '10-28', // Den vzniku samostatného československého státu
  '11-17', // Den boje za svobodu a demokracii
  '12-24', // Štědrý den
  '12-25', // 1. svátek vánoční
  '12-26', // 2. svátek vánoční
]

/**
 * Easter Sunday, as a UTC-midnight anchor — the anonymous Gregorian algorithm.
 *
 * Computed rather than tabulated because a hand-written table expires in
 * silence, and the failure it expires into is precisely the one this calendar
 * exists to prevent: a confident delivery promise for a day the warehouse is
 * shut. Good Friday and Easter Monday are the two Czech holidays that move.
 */
function easterSunday(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1

  return new Date(Date.UTC(year, month - 1, day))
}

/** `YYYY-MM-DD` for a UTC-midnight anchor, without going through a locale. */
function isoDay(day: Date): string {
  return day.toISOString().slice(0, 10)
}

/** Memoized per year: the estimate asks about a handful of days, repeatedly. */
const holidaysByYear = new Map<number, Set<string>>()

function warehouseHolidaysFor(year: number): Set<string> {
  const cached = holidaysByYear.get(year)
  if (cached)
    return cached

  const easter = easterSunday(year)
  const shifted = (days: number) => {
    const moved = new Date(easter)
    moved.setUTCDate(moved.getUTCDate() + days)
    return isoDay(moved)
  }

  const days = new Set([
    ...FIXED_WAREHOUSE_HOLIDAYS.map(monthDay => `${year}-${monthDay}`),
    shifted(-2), // Velký pátek
    shifted(1), // Velikonoční pondělí
  ])

  holidaysByYear.set(year, days)
  return days
}

/**
 * Whether the warehouse is closed for a public holiday on `day`, which must be
 * a UTC-midnight anchor — the form `useDeliveryEstimate` works in, so that a
 * DST shift can never move the comparison.
 */
export function isWarehouseHoliday(day: Date): boolean {
  return warehouseHolidaysFor(day.getUTCFullYear()).has(isoDay(day))
}

/**
 * Transit ranges come from the delivery pages, and every per-market figure in
 * `useDeliveryEstimate.TRANSIT_WORKING_DAYS` sits inside its range (cs 1 in
 * 1–2, sk 2 in 1–2, pl 2 in 1–2, de 2 in 2–5). Keep it that way: the estimate
 * promises a single date, this promises the window Google publishes, and a
 * date outside the published window is the one combination customers notice.
 */
export const MARKET_SHIPPING: Record<MarketCountry, MarketShipping> = {
  // 69 Kč — PPL Parcelshop / Zásilkovna výdejní místo, the cheapest CZ tier.
  CZ: { country: 'CZ', currency: 'CZK', baseRateMinor: 6900, transitDays: { min: 1, max: 2 }, shippingPage: '/delivery' },
  // 3,00 € — PPL Parcelshop / Packeta, pickup or courier alike.
  SK: { country: 'SK', currency: 'EUR', baseRateMinor: 300, transitDays: { min: 1, max: 2 }, shippingPage: '/delivery' },
  // 8,99 zł — InPost Paczkomaty / DHL POP / Poczta Polska pickup.
  PL: { country: 'PL', currency: 'PLN', baseRateMinor: 899, transitDays: { min: 1, max: 2 }, shippingPage: '/delivery' },
  // 6,00 € — DHL and Hermes, home delivery or pickup station, all one price.
  DE: { country: 'DE', currency: 'EUR', baseRateMinor: 600, transitDays: { min: 2, max: 5 }, shippingPage: '/delivery' },
  // 7,00 € — DHL Lieferung, the cheapest standard Austrian tier (Hermes 7,50 €).
  AT: { country: 'AT', currency: 'EUR', baseRateMinor: 700, transitDays: { min: 2, max: 5 }, shippingPage: '/delivery' },
}

/**
 * The voluntary 30-day, seller-paid return programme, on top of the statutory
 * withdrawal right. Published in full at `content/de/return-policy.md` (§2 and
 * §4.6); the cs/sk/pl pages promise the free carriage label but still describe
 * only the statutory 14-day window, so those pages have to be brought in line
 * with this table — `merchantReturnLink` points Google straight at them.
 */
export const MARKET_RETURNS: MarketReturns[] = [
  { countries: ['CZ', 'SK', 'PL', 'DE', 'AT'], windowDays: 30, free: true, policyPage: '/return-policy' },
]

/**
 * The return window the storefront quotes shoppers OUTSIDE the programme above:
 * every locale `returnsPolicyForLocale` finds no row for (en, eu, it, es, fr,
 * dk, nl today). Their free return is conditional — full-price items only, and
 * never socks, whose return carriage the shopper pays — so every claim of it
 * carries an asterisk and links to the terms clause that says so
 * (`useReturnsClaim`, `FREE_RETURNS_TERMS_ANCHOR`).
 *
 * Display-only, and deliberately NOT a `MARKET_RETURNS` row: that table feeds
 * the JSON-LD's `hasMerchantReturnPolicy`, where a row would publish an
 * unconditional `FreeReturn` to Google — false for these markets.
 *
 * Still flagged for the content owner: `content/en/return-policy.md`, which
 * `en` and `eu` read, says the buyer bears the return carriage, so the claim
 * is not yet backed there, and the terms clause it links to is not written.
 */
export const CONDITIONAL_FREE_RETURNS_WINDOW_DAYS = 30

/**
 * Destinations each shipped locale publishes policy for. `de` serves Germany
 * AND Austria — the two have different rates, which is why both tables are
 * keyed by country rather than by locale.
 *
 * `en` is deliberately empty. It is the USA storefront: no courier config, no
 * free-shipping threshold, and its own return page says the buyer pays the
 * return carriage. Publishing a rate or a free-return claim there would be
 * false, so it gets neither.
 *
 * `eu` is empty for the opposite reason: it ships to 39 destinations at rates
 * that differ per country, and these tables publish ONE figure per locale. The
 * checkout prices the real destination from the order address either way, so
 * the only thing an entry here would add is a number that is wrong for 38 of
 * them. Give it rows once the market narrows to destinations we can state.
 */
export const MARKET_COUNTRIES_BY_LOCALE: Record<string, MarketCountry[]> = {
  cs: ['CZ'],
  sk: ['SK'],
  pl: ['PL'],
  de: ['DE', 'AT'],
  en: [],
  eu: [],
  // it/es/fr/dk/nl each serve exactly one country and could state a real figure — but
  // the tables above hold none for them: no base rate, no transit window, and
  // the 30-day seller-paid return programme covers CZ/SK/PL/DE/AT only. Adding
  // a country here without rows in `MARKET_SHIPPING` and `MARKET_RETURNS` is a
  // type error, and adding invented rows would publish a claim (and a
  // `merchantReturnLink`) we have not agreed to honour. Empty until the rates
  // and the return window for these markets are settled.
  it: [],
  es: [],
  fr: [],
  dk: [],
  nl: [],
}

/** Destinations this locale ships to, empty for markets with no configuration. */
export function marketCountriesForLocale(locale: string): MarketCountry[] {
  return MARKET_COUNTRIES_BY_LOCALE[locale] ?? []
}

/** The return terms covering a destination, or `undefined` where none apply. */
export function returnsPolicyForCountry(country: MarketCountry): MarketReturns | undefined {
  return MARKET_RETURNS.find(policy => policy.countries.includes(country))
}

/** The return terms a locale can advertise, or `undefined` (e.g. `en`). */
export function returnsPolicyForLocale(locale: string): MarketReturns | undefined {
  const country = marketCountriesForLocale(locale)[0]
  return country ? returnsPolicyForCountry(country) : undefined
}
